import { ApiError, errorMessage, newIdempotencyKey } from '@runnerboy/api-client';
import type { AddressDto, ComplianceBlockDto, PricingBreakdownDto } from '@runnerboy/types';
import { Alert, Button, cx, EmptyState, Input, inr, ProductImage, QuantityStepper, Skeleton, useToast, volume } from '@runnerboy/ui';
import { ArrowLeft, ArrowRight, BadgeCheck, Building2, CheckCircle2, CreditCard, Hotel, Landmark, Lock, MapPin, ShieldCheck, ShoppingBag, Smartphone, Tag, Wallet } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { BillDetails, ComplianceBlocks, stageStyle, useCartActions } from '../components/commerce';
import { AgeVerifySheet, LocationSheet, useMe } from '../components/sheets';
import { Page } from '../components/shell';
import { client, useAsync, useSession } from '../lib/session';

const STEPS = ['cart', 'checkout', 'pay', 'track'] as const;

/** Header for the focused order flow: back, title, and where the customer is in the journey. */
export function FlowHeader({ title, subtitle, step, onBack }: { title: string; subtitle?: ReactNode; step: (typeof STEPS)[number]; onBack?: () => void }) {
  const { t } = useTranslation();
  const nav = useNavigate();
  const idx = STEPS.indexOf(step);
  return (
    <div className="sticky z-20 -mx-4 -mt-4 bg-paper/95 px-4 pb-3 pt-3 backdrop-blur-xl" style={{ top: 'var(--rb-header-h, 0px)' }}>
      <div className="flex items-center gap-3">
        <button onClick={onBack ?? (() => nav(-1))} className="rb-press grid h-10 w-10 shrink-0 place-items-center rounded-full bg-white shadow-[var(--shadow-card)]" aria-label={t('common.back')}>
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="min-w-0 flex-1">
          <h1 className="rb-display truncate text-[22px] font-bold leading-tight text-ink-900">{title}</h1>
          {subtitle && <p className="truncate text-[12.5px] text-ink-500">{subtitle}</p>}
        </div>
      </div>
      <ol className="mt-3 grid grid-cols-4 gap-1.5" aria-label="Progress">
        {STEPS.map((s, i) => (
          <li key={s} className="flex flex-col gap-1">
            <span className={cx('h-1 rounded-full', i < idx ? 'bg-ink-900' : i === idx ? 'bg-marigold-500' : 'bg-ink-200')} />
            <span className={cx('rb-eyebrow text-[9px]', i <= idx ? 'text-ink-700' : 'text-ink-400')}>{t(`flow.${s}`, s)}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}

/** Sticky bottom action area for flow screens (there is no tab bar in the flow). */
function ActionBar({ children, note }: { children: ReactNode; note?: ReactNode }) {
  return (
    <div className="fixed inset-x-0 bottom-0 z-30 rounded-t-[26px] bg-white px-4 pt-3 shadow-[var(--shadow-sheet)]" style={{ paddingBottom: 'max(14px, calc(env(safe-area-inset-bottom) + 10px), calc(var(--safe-area-inset-bottom, 0px) + 10px))' }}>
      <div className="mx-auto max-w-3xl">
        {note && <div className="mb-2 flex items-center justify-center gap-1.5 text-[11.5px] text-ink-500">{note}</div>}
        {children}
      </div>
    </div>
  );
}

export function CartPage() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const { cart, user, setLoginOpen, refreshCart, location } = useSession();
  const actions = useCartActions();
  const toast = useToast();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void refreshCart();
  }, [refreshCart]);

  if (!user) {
    return (
      <Page flow>
        <FlowHeader title={t('cart.title')} step="cart" />
        <EmptyState icon={<ShoppingBag className="h-8 w-8" />} title={t('auth.loginTitle')} action={<Button onClick={() => setLoginOpen(true)}>{t('common.continue')}</Button>} />
      </Page>
    );
  }
  if (!cart)
    return (
      <Page flow>
        <FlowHeader title={t('cart.title')} step="cart" />
        <Skeleton className="mt-3 h-60 rounded-[22px]" />
      </Page>
    );
  if (cart.items.length === 0) {
    return (
      <Page flow>
        <FlowHeader title={t('cart.title')} step="cart" />
        <EmptyState icon={<ShoppingBag className="h-8 w-8" />} title={t('cart.empty')} body={t('cart.emptyBody')} action={<Button variant="accent" onClick={() => nav('/')}>{t('cart.browse')}</Button>} />
      </Page>
    );
  }
  const applyCoupon = async (value: string | null) => {
    setBusy(true);
    try {
      await client.post('/cart/coupon', { code: value, addressId: location?.addressId ?? null });
      await refreshCart();
      if (value) toast('success', `${value.toUpperCase()} applied`);
      setCode('');
    } catch (e) {
      toast('danger', errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  const count = cart.items.reduce((s, i) => s + i.quantity, 0);
  return (
    <Page flow>
      <FlowHeader title={t('cart.title')} subtitle={t('cart.fromStore', { store: cart.storeName })} step="cart" />

      <section className="mt-2 flex flex-col gap-2.5">
        {cart.items.map((i) => (
          <div key={i.id} className="flex items-center gap-3 rounded-[22px] bg-white p-2.5 pr-3 shadow-[var(--shadow-card)]">
            <span className="h-[68px] w-[68px] shrink-0 overflow-hidden rounded-[16px] p-1.5" style={stageStyle(i.product.categorySlug)}>
              <ProductImage url={i.product.imageUrl} categorySlug={i.product.categorySlug} alt={i.product.name} brand={i.product.brand} volumeMl={i.product.volumeMl} className="h-full w-full drop-shadow-[0_6px_8px_rgb(14_19_36/0.2)]" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="line-clamp-2 text-[14px] font-semibold leading-tight text-ink-900">{i.product.name}</div>
              <div className="mt-1 flex items-center gap-1.5">
                {i.product.volumeMl && <span className="rb-num rounded-full bg-ink-900/[0.05] px-2 py-0.5 text-[11px] text-ink-600">{volume(i.product.volumeMl)}</span>}
                {i.product.isAgeRestricted && <span className="rb-num rounded-full bg-ink-900 px-2 py-0.5 text-[10.5px] font-semibold text-white">21+</span>}
              </div>
              <div className="rb-display mt-1 text-[17px] font-bold text-ink-900">{inr(i.lineTotal)}</div>
            </div>
            <QuantityStepper value={i.quantity} onChange={(v) => void actions.setQty(i.product, v)} max={Math.min(i.product.available + i.quantity, 24)} label={i.product.name} busy={actions.busy === i.storeProductId} />
          </div>
        ))}
      </section>

      <section className="mt-4 rounded-[22px] border-2 border-dashed border-ink-200 bg-white p-4">
        <div className="flex items-center gap-2">
          <Tag className="h-[18px] w-[18px] text-marigold-600" aria-hidden />
          <span className="text-[14px] font-bold text-ink-900">{t('cart.coupon')}</span>
        </div>
        {cart.couponCode ? (
          <div className="mt-3 flex items-center justify-between rounded-[14px] bg-success-50 px-3 py-2.5">
            <span className="rb-num text-[14px] font-bold text-success-600">{cart.couponCode} ✓</span>
            <Button size="sm" variant="ghost" onClick={() => void applyCoupon(null)} loading={busy}>
              {t('cart.remove')}
            </Button>
          </div>
        ) : (
          <form
            className="mt-3 flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              void applyCoupon(code);
            }}
          >
            <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder={t('cart.couponPlaceholder')} aria-label={t('cart.couponPlaceholder')} className="rb-num h-11 rounded-full uppercase" />
            <Button type="submit" variant="primary" loading={busy} disabled={code.length < 3}>
              {t('cart.apply')}
            </Button>
          </form>
        )}
      </section>

      {cart.pricing && (
        <div className="mt-4">
          <BillDetails pricing={cart.pricing} storeName={cart.storeName} />
        </div>
      )}
      {cart.compliance && !cart.compliance.allowed && (
        <div className="mt-4">
          <ComplianceBlocks blocks={cart.compliance.blocks.filter((b) => !['CUSTOMER_AGE_NOT_VERIFIED', 'CUSTOMER_NOT_AUTHENTICATED'].includes(b.code))} />
        </div>
      )}
      <ActionBar
        note={
          <>
            <ShieldCheck className="h-3.5 w-3.5 text-success-600" aria-hidden /> {t('cart.sealedNote', 'Sealed by the store · ID checked at your door')}
          </>
        }
      >
        <Button size="lg" variant="accent" block onClick={() => nav('/checkout')}>
          <span className="flex w-full items-center justify-between">
            <span className="text-left text-[13px] font-semibold leading-tight">
              <span className="rb-num block text-[17px] font-bold">{inr(cart.pricing?.total ?? 0)}</span>
              {t('common.items', { count })}
            </span>
            <span className="inline-flex items-center gap-1.5 text-[16px] font-bold">
              {t('cart.checkoutShort', 'Checkout')} <ArrowRight className="h-5 w-5" aria-hidden />
            </span>
          </span>
        </Button>
      </ActionBar>
    </Page>
  );
}

const METHODS = [
  { id: 'UPI', icon: Smartphone },
  { id: 'CARD', icon: CreditCard },
  { id: 'NETBANKING', icon: Landmark },
  { id: 'WALLET', icon: Wallet },
] as const;
type Method = (typeof METHODS)[number]['id'];

interface Preview {
  pricing: PricingBreakdownDto;
  compliance: { allowed: boolean; blocks: ComplianceBlockDto[]; notices: string[] };
  minimumAge: number | null;
  handoverDocuments: string[];
  store: { id: string; name: string; legalEntityName: string };
  couponError: string | null;
}

function Step({ n, title, done, children, action }: { n: number; title: string; done?: boolean; children: ReactNode; action?: ReactNode }) {
  return (
    <section className="mt-4 rounded-[24px] bg-white p-4 shadow-[var(--shadow-card)]">
      <div className="mb-3 flex items-center gap-2.5">
        <span className={cx('rb-num grid h-7 w-7 shrink-0 place-items-center rounded-full text-[12px] font-bold', done ? 'bg-ink-900 text-marigold-500' : 'bg-ink-100 text-ink-700')}>{done ? <CheckCircle2 className="h-4 w-4" /> : n}</span>
        <h2 className="flex-1 text-[15px] font-bold text-ink-900">{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}

const addressLine = (a: AddressDto) => [a.flat, a.floor && `Floor ${a.floor}`, a.building, a.street, a.locality, a.city, a.pincode].filter(Boolean).join(', ');

export function CheckoutPage() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const toast = useToast();
  const { location, setLocation, refreshCart, cart } = useSession();
  const { me, reload: reloadMe } = useMe();
  const addresses = useAsync((s) => client.get<AddressDto[]>('/me/addresses', s), []);
  const [addressId, setAddressId] = useState<string | null>(location?.addressId ?? null);
  const [method, setMethod] = useState<Method>('UPI');
  const [tip, setTip] = useState(0);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [placing, setPlacing] = useState(false);
  const [blocks, setBlocks] = useState<ComplianceBlockDto[]>([]);
  const [ageOpen, setAgeOpen] = useState(false);
  const [locOpen, setLocOpen] = useState(false);
  const [changing, setChanging] = useState(false);
  const key = useRef(newIdempotencyKey());

  useEffect(() => {
    if (!addressId && addresses.data?.length) setAddressId(addresses.data.find((a) => a.isDefault)?.id ?? addresses.data[0]?.id ?? null);
  }, [addresses.data, addressId]);

  useEffect(() => {
    if (!addressId) return;
    setPreviewError(null);
    client
      .post<Preview>('/checkout/preview', { addressId, fulfilmentMode: 'DELIVERY', paymentMethod: method, tipMinor: tip, couponCode: cart?.couponCode ?? null })
      .then(setPreview)
      .catch((e) => setPreviewError(errorMessage(e)));
    // A change in what is being bought produces a new idempotency key (a different order).
    key.current = newIdempotencyKey();
  }, [addressId, method, tip, cart?.couponCode, me?.ageVerification?.verified]);

  const ageVerified = !!me?.ageVerification?.verified;
  const selectedAddress = addresses.data?.find((a) => a.id === addressId) ?? null;
  const ageRelatedOnly = (preview?.compliance.blocks ?? []).every((b) => ['CUSTOMER_AGE_NOT_VERIFIED'].includes(b.code));

  const place = async () => {
    setPlacing(true);
    setBlocks([]);
    try {
      const r = await client.post<{ order: { id: string; status: string }; payment: { status: string; provider: string } | null }>(
        '/checkout/orders',
        { addressId, fulfilmentMode: 'DELIVERY', paymentMethod: method, tipMinor: tip, couponCode: cart?.couponCode ?? null, ageConfirmed: true },
        { idempotencyKey: key.current },
      );
      await refreshCart();
      if (r.order.status === 'PAYMENT_PENDING') nav(`/pay/${r.order.id}`, { replace: true });
      else nav(`/orders/${r.order.id}`, { replace: true });
    } catch (e) {
      if (e instanceof ApiError && e.complianceBlocks) setBlocks(e.complianceBlocks);
      else toast('danger', errorMessage(e));
    } finally {
      setPlacing(false);
    }
  };

  const chooseAddress = (a: AddressDto) => {
    setAddressId(a.id);
    setChanging(false);
    setLocation({ lat: a.location.lat, lng: a.location.lng, label: `${t(`address.${a.label}`)} · ${a.locality}`, addressId: a.id });
  };

  return (
    <Page flow>
      <FlowHeader title={t('checkout.title')} subtitle={preview?.store.name} step="checkout" />

      <Step
        n={1}
        title={t('checkout.deliveryAddress')}
        done={!!selectedAddress}
        action={
          selectedAddress && (addresses.data?.length ?? 0) > 1 ? (
            <button className="rb-press rounded-full bg-ink-900/[0.05] px-3 py-1.5 text-[12.5px] font-semibold text-ink-900" onClick={() => setChanging((c) => !c)}>
              {changing ? t('common.cancel') : t('common.change')}
            </button>
          ) : undefined
        }
      >
        {addresses.loading ? (
          <Skeleton className="h-20 rounded-[16px]" />
        ) : (addresses.data ?? []).length === 0 ? (
          <Button variant="secondary" block onClick={() => setLocOpen(true)}>
            <MapPin className="h-4 w-4" /> {t('checkout.chooseAddress')}
          </Button>
        ) : (
          <div className="flex flex-col gap-2" role="radiogroup">
            {(changing ? addresses.data ?? [] : selectedAddress ? [selectedAddress] : []).map((a) => {
              const active = addressId === a.id;
              const Icon = a.label === 'HOTEL' ? Hotel : a.label === 'WORK' ? Building2 : MapPin;
              return (
                <button key={a.id} role="radio" aria-checked={active} onClick={() => chooseAddress(a)} className={cx('rb-press flex items-start gap-3 rounded-[18px] p-3 text-left', active ? 'bg-marigold-50 ring-2 ring-marigold-500' : 'bg-ink-50 ring-1 ring-ink-100')}>
                  <span className={cx('grid h-10 w-10 shrink-0 place-items-center rounded-full', active ? 'bg-ink-900 text-marigold-500' : 'bg-white text-ink-600')}>
                    <Icon className="h-[18px] w-[18px]" aria-hidden />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-[14px] font-bold text-ink-900">
                      {t(`address.${a.label}`)} {a.hotelName && `· ${a.hotelName}`}
                    </span>
                    <span className="mt-0.5 block text-[12.5px] leading-snug text-ink-500">{addressLine(a)}</span>
                  </span>
                </button>
              );
            })}
            {changing && (
              <Button variant="ghost" size="sm" onClick={() => setLocOpen(true)} className="self-start">
                + {t('address.title')}
              </Button>
            )}
          </div>
        )}
        {selectedAddress?.label === 'HOTEL' && <Alert tone="info" className="mt-3">{t('address.hotelNote')}</Alert>}
      </Step>

      <Step n={2} title={t('checkout.ageTitle')} done={ageVerified}>
        {ageVerified ? (
          <div className="flex items-center gap-2 text-[14px] font-semibold text-success-600">
            <BadgeCheck className="h-5 w-5" /> {t('checkout.ageVerified')}
            {preview?.minimumAge && <span className="rb-num ml-auto rounded-full bg-ink-900 px-2 py-0.5 text-[11px] font-semibold text-white">{preview.minimumAge}+</span>}
          </div>
        ) : (
          <>
            <p className="text-[13.5px] leading-relaxed text-ink-600">{t('checkout.ageBody')}</p>
            <Button className="mt-3" onClick={() => setAgeOpen(true)}>
              {t('checkout.verifyAge')}
            </Button>
          </>
        )}
      </Step>

      <Step n={3} title={t('checkout.payment')} done>
        <div className="grid grid-cols-2 gap-2" role="radiogroup">
          {METHODS.map((m) => (
            <button
              key={m.id}
              role="radio"
              aria-checked={method === m.id}
              onClick={() => setMethod(m.id)}
              className={cx('rb-press flex h-[58px] items-center gap-2.5 rounded-[16px] px-3 text-left text-[13.5px] font-semibold', method === m.id ? 'bg-ink-900 text-white' : 'bg-ink-50 text-ink-700 ring-1 ring-ink-100')}
            >
              <span className={cx('grid h-8 w-8 place-items-center rounded-full', method === m.id ? 'bg-marigold-500 text-ink-950' : 'bg-white text-ink-600')}>
                <m.icon className="h-4 w-4" aria-hidden />
              </span>
              {t(`checkout.${m.id}`)}
            </button>
          ))}
        </div>
      </Step>

      <Step n={4} title={t('checkout.tip')} done={tip > 0}>
        <p className="-mt-1 mb-3 text-[12.5px] text-ink-500">{t('checkout.tipNote')}</p>
        <div className="flex gap-2">
          {[0, 2000, 3000, 5000].map((v) => (
            <button key={v} onClick={() => setTip(v)} aria-pressed={tip === v} className={cx('rb-press h-10 flex-1 rounded-full text-[13px] font-semibold', tip === v ? 'bg-ink-900 text-white' : 'bg-ink-50 text-ink-700 ring-1 ring-ink-100')}>
              {v === 0 ? t('checkout.noTip') : <span className="rb-num">{inr(v)}</span>}
            </button>
          ))}
        </div>
      </Step>

      {preview && (
        <div className="mt-4 flex flex-col gap-3">
          <BillDetails pricing={preview.pricing} storeName={preview.store.legalEntityName} />
          {preview.minimumAge && (
            <div className="flex gap-3 rounded-[20px] bg-peacock-50 p-4 text-[13px] leading-relaxed text-peacock-800">
              <ShieldCheck className="h-5 w-5 shrink-0" aria-hidden />
              {t('checkout.handoverNotice', { documents: preview.handoverDocuments.map((d) => d.replace('_', ' ').toLowerCase()).join(', ') })}
            </div>
          )}
          {!preview.compliance.allowed && !ageRelatedOnly && <ComplianceBlocks blocks={preview.compliance.blocks} />}
          {preview.couponError && <Alert tone="warning">{preview.couponError}</Alert>}
          <div className="flex items-center gap-3 rounded-[20px] bg-night-900 p-4">
            <span className="rb-display text-[15px] font-extrabold leading-tight text-marigold-500">{t('safety.line1', 'DON’T DRINK & DRIVE.')}</span>
            <span className="text-[12.5px] leading-snug text-white/75">{t('safety.checkoutLine', 'Stay where you are — we’ll bring it.')}</span>
          </div>
          <p className="px-1 text-[11.5px] leading-relaxed text-ink-500">
            {t('checkout.sellerNotice', { store: preview.store.legalEntityName })} {t('checkout.termsNotice')}
          </p>
        </div>
      )}
      {previewError && <Alert tone="danger" className="mt-4">{previewError}</Alert>}
      {blocks.length > 0 && (
        <div className="mt-4">
          <ComplianceBlocks blocks={blocks} />
        </div>
      )}

      <ActionBar
        note={
          <>
            <Lock className="h-3.5 w-3.5" aria-hidden /> {t('checkout.secure', 'Secure payment · alcohol is sold by the licensed store')}
          </>
        }
      >
        <Button size="lg" variant="accent" block loading={placing} disabled={!preview || !addressId || !ageVerified || !preview.compliance.allowed} onClick={() => void place()}>
          {t('checkout.placeOrder', { total: inr(preview?.pricing.total ?? 0) })}
        </Button>
      </ActionBar>
      <AgeVerifySheet
        open={ageOpen}
        onClose={() => setAgeOpen(false)}
        onVerified={() => {
          setAgeOpen(false);
          reloadMe();
        }}
      />
      <LocationSheet
        open={locOpen}
        onClose={() => {
          setLocOpen(false);
          addresses.reload();
        }}
      />
    </Page>
  );
}
